import { createNewHosts, createInputTransfer, removeNgStyles } from '@angularclass/hmr';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Optional, Inject, Injectable, Input, Directive, NgModule, enableProdMode, ApplicationRef, SkipSelf } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import * as i2 from '@citi-gcg-173875/citi-utils';
import { AppConfigService, FeatureDetectionService, StorageService, SessionService } from '@citi-gcg-173875/citi-utils';
import { of, fromEvent, timer, Subject, BehaviorSubject, catchError, throwError } from 'rxjs';
import { map, switchMap, mergeMap, tap, take } from 'rxjs/operators';
import * as i1 from '@citi-gcg-173875/citi-http';
import { HttpConfigService, ContentStorageService, ContentStaticService, HttpService, ContentService, ContentResolve } from '@citi-gcg-173875/citi-http';
import { SESSION_STORAGE, provideNgxWebstorage, withNgxWebstorageConfig, withLocalStorage, withSessionStorage } from 'ngx-webstorage';
import { disableDebugTools, enableDebugTools } from '@angular/platform-browser';

/**
 * module CoreAppModule
 * main CoreAppModule
 * preferred
 **/
/** CoreAppModule **/
class CoreAppModule {
    constructor(appRef, appStateService) {
        this.appRef = appRef;
        this.appStateService = appStateService;
    }
    hmrOnInit(store) {
        if (!store || !store.state)
            return;
        console.log('HMR store', JSON.stringify(store, null, 2));
        // set state
        this.appStateService._state = store.state;
        // set input values
        if ('restoreInputValues' in store) {
            let restoreInputValues = store.restoreInputValues;
            setTimeout(restoreInputValues);
        }
        this.appRef.tick();
        delete store.state;
        delete store.restoreInputValues;
    }
    hmrOnDestroy(store) {
        const cmpLocation = this.appRef.components.map(cmp => cmp.location.nativeElement);
        // save state
        const state = this.appStateService._state;
        store.state = state;
        // recreate root elements
        store.disposeOldHosts = createNewHosts(cmpLocation);
        // save input values
        store.restoreInputValues = createInputTransfer();
        // remove styles
        removeNgStyles();
    }
    hmrAfterDestroy(store) {
        // display new elements
        store.disposeOldHosts();
        delete store.disposeOldHosts;
    }
}

class EventHubProducerService {
    static { this.eventHubUrl = '/retailbank/gcg/event/postEvent'; }
    constructor(_httpService, _sessionService, _window, _appConfigService, _sessionStorage) {
        this._httpService = _httpService;
        this._sessionService = _sessionService;
        this._window = _window;
        this._appConfigService = _appConfigService;
        this._sessionStorage = _sessionStorage;
    }
    postToEventHub(eventName, customPayload, successHandler, errorHandler) {
        if (!this.ehFallbackMode()) {
            let payloadBody = {
                event: {
                    body: Object.assign({}, this.generateGenericPayloadDetails(), this.generateCustomPayload(customPayload))
                }
            };
            return this._httpService.post(EventHubProducerService.eventHubUrl, payloadBody, {
                headers: {
                    eventName: eventName
                }
            });
        }
        return of("event hub fallback mode enabled...");
    }
    generateGenericPayloadDetails() {
        if (!!this._window.nativeWindow) {
            return {
                deviceDetails: {
                    platform: this._window.nativeWindow.citiData.platform,
                    device_version: '',
                    os_version: '',
                    app_ver: this._appConfigService.getHttpServiceConfig().defaultHeaders
                        .appVersion,
                    cust_type: this._window.nativeWindow.citiData.custType,
                    bank_relationgroup: '',
                    lob: this._window.nativeWindow.citiData.lob,
                    site_section1: this._window.nativeWindow.citiData.siteSection1,
                    geo_location: 'null'
                }
            };
        }
    }
    generateCustomPayload(customPayload) {
        return {
            eventDetails: customPayload
        };
    }
    ehFallbackMode() {
        if (!this._sessionStorage) {
            return true;
        }
        else {
            try {
                let spbs = JSON.parse(this._sessionStorage.getItem('sessionPostBrandingStorage'));
                let eventHubActive = spbs['eventHub_qa'];
                if (!eventHubActive) {
                    return true;
                }
                return false;
            }
            catch (e) {
                console.error('could not locate spbs or fallback');
                return true;
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EventHubProducerService, deps: [{ token: i1.HttpService }, { token: i2.PostSessionService }, { token: i2.WindowRef }, { token: i2.AppConfigService }, { token: SESSION_STORAGE, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EventHubProducerService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EventHubProducerService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpService }, { type: i2.PostSessionService }, { type: i2.WindowRef }, { type: i2.AppConfigService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SESSION_STORAGE]
                }] }] });

class EventHubActionProducerDirective {
    set feature(feat) {
        this._feature = feat;
    }
    set hoverActive(thing) {
        this._hoverActive = thing;
    }
    set clickActive(thing) {
        this._clickActive = thing;
    }
    set topic(topic) {
        this._topic = topic;
    }
    set payload(payload) {
        this._payload = payload;
    }
    set eventType(event) {
        this._eventType = event;
    }
    constructor(_ehProducerService, _el, _windowRef, _zone) {
        this._ehProducerService = _ehProducerService;
        this._el = _el;
        this._windowRef = _windowRef;
        this._zone = _zone;
        this._payload = {};
        this._hoverDuration = 1500;
        this._hoverActive = false;
        this._clickActive = true;
        this.generateHoverListener = el => {
            return (fromEvent(el, 'mouseenter')
                // this._hoverSubscription = of(Date.now())
                .pipe(map(() => Date.now()), switchMap(startTime => {
                return fromEvent(el, 'mouseleave').pipe(mergeMap(event => {
                    let duration = Date.now() - startTime;
                    if (duration > this._hoverDuration) {
                        // this._hoverSubscription.unsubscribe();
                        return this._ehProducerService
                            .postToEventHub(this._topic, this.generateHoverPayload(event, duration))
                            .pipe(tap(() => {
                            this._hoverSubscription.unsubscribe();
                        }));
                    }
                    else {
                        return of(false);
                    }
                }));
            })));
        };
        this.generateClickListener = el => {
            return fromEvent(el, 'click').pipe(tap(val => {
                // console.log('click val');
                // console.log(val);
            }), switchMap((val) => {
                return this._ehProducerService.postToEventHub(this._topic, this.generateClickPayload(val));
            }));
        };
        if (!this._windowRef.nativeWindow) {
            throw new Error('refusing to spin up listeners on server side');
        }
    }
    ngAfterViewInit() {
        this._zone.runOutsideAngular(() => {
            if (this._hoverActive) {
                this._hoverSubscription = this.generateHoverListener(this._el.nativeElement).subscribe();
            }
            this._clickSubscription = this.generateClickListener(this._el.nativeElement).subscribe();
        });
    }
    ngOnDestroy() {
        timer(1000).pipe(take(1)).subscribe(int => {
            this._clickSubscription ? this._clickSubscription.unsubscribe() : '';
            this._hoverSubscription ? this._hoverSubscription.unsubscribe() : '';
        });
    }
    generateClickPayload(event) {
        let elementID = event.target.id;
        let eventtype = 'click';
        if (this._eventType) {
            eventtype = this._eventType;
        }
        return {
            eventType: eventtype,
            xCoordinate: event.x,
            yCoordinate: event.y,
            elementID: this._feature,
            // feature: this._feature
        };
    }
    generateHoverPayload(event, duration) {
        let elementID = event.target.id;
        return {
            eventType: 'hover',
            xCoordinate: event.x,
            yCoordinate: event.y,
            elementID: this._feature,
            // feature: this._feature,
            duration: duration
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EventHubActionProducerDirective, deps: [{ token: EventHubProducerService }, { token: i0.ElementRef }, { token: i2.WindowRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.14", type: EventHubActionProducerDirective, isStandalone: false, selector: "[citiEventHubActionProducer]", inputs: { feature: "feature", hoverActive: ["hover_active", "hoverActive"], clickActive: ["click_active", "clickActive"], topic: "topic", payload: "payload", eventType: "eventType" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: EventHubActionProducerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[citiEventHubActionProducer]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: EventHubProducerService }, { type: i0.ElementRef }, { type: i2.WindowRef }, { type: i0.NgZone }], propDecorators: { feature: [{
                type: Input
            }], hoverActive: [{
                type: Input,
                args: ['hover_active']
            }], clickActive: [{
                type: Input,
                args: ['click_active']
            }], topic: [{
                type: Input
            }], payload: [{
                type: Input
            }], eventType: [{
                type: Input
            }] } });

/**
 * module CoreCommonModule
 * main CoreCommonModule
 * preferred
 **/
/** CoreCommonModule **/
// import { ContentComponent } from './components/content.component';
class CoreCommonModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CoreCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: CoreCommonModule, declarations: [
            //ContentComponent
            EventHubActionProducerDirective], imports: [CommonModule], exports: [
            // Modules
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            RouterModule,
            EventHubActionProducerDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CoreCommonModule, imports: [CommonModule, 
            // Modules
            CommonModule,
            FormsModule,
            ReactiveFormsModule,
            RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CoreCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [
                        // Modules
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        RouterModule,
                        EventHubActionProducerDirective
                    ],
                    declarations: [
                        //ContentComponent
                        EventHubActionProducerDirective
                    ]
                }]
        }] });

/**
 * module CoreDecorateModuleRef
 * main CoreDecorateModuleRef
 * preferred
 **/
/** CoreDecorateModuleRef **/
class CoreDecorateModuleRef {
    constructor(environment) {
        // Angular debug tools in the dev console
        // https://github.com/angular/angular/blob/86405345b781a9dc2438c0fbe3e9409245647019/TOOLS_JS.md
        this._decorateModuleRef = function identity(value) {
            return value;
        };
        if ('production' === environment ||
            'uat' === environment ||
            'sit' === environment) {
            // Production
            this._decorateModuleRef = (modRef) => {
                disableDebugTools();
                enableProdMode();
            };
        }
        else {
            this._decorateModuleRef = (modRef) => {
                const appRef = modRef.injector.get(ApplicationRef);
                const cmpRef = appRef.components[0];
                let _ng = window.ng;
                enableDebugTools(cmpRef);
                window.ng.probe = _ng.probe;
                window.ng.coreTokens = _ng.coreTokens;
                return modRef;
            };
        }
    }
    getDecorateModuleRef() {
        return this._decorateModuleRef;
    }
}

/**
 * module CoreServicesModule
 **/
/** AppStateService **/
/**
 * The AppStateService
 * class AppStateService
 **/
class AppStateService {
    constructor() {
        this._state = {};
    }
    // already return a clone of the current state
    get state() {
        return (this._state = this._clone(this._state));
    }
    // never allow mutation
    set state(value) {
        throw new Error('do not mutate the `.state` directly');
    }
    get(prop) {
        // use our state getter for the clone
        const state = this.state;
        return state.hasOwnProperty(prop) ? state[prop] : state;
    }
    set(prop, value) {
        // internally mutate our state
        return (this._state[prop] = value);
    }
    _clone(object) {
        // simple object clone
        return JSON.parse(JSON.stringify(object));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: AppStateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: AppStateService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: AppStateService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class ShoppingCartService {
    constructor(_httpService) {
        this._httpService = _httpService;
        this.retrivedCartBs$ = new Subject();
        this.augeoURL$ = new BehaviorSubject('');
        this.augeoError$ = new BehaviorSubject(false);
        this.isLoading$ = new BehaviorSubject(false);
        this.disableCheckOut$ = new BehaviorSubject(false);
        this.catalogCardList$ = new Subject();
        this.catalogCardDetails$ = new Subject();
        this.init();
    }
    init() {
        // this.getShoppingCartData();
    }
    errorMessage(errRes) {
        if (errRes.errorCode == '402') {
            // this.dailyDealAddToCartErrors = 'sold-out';
            return;
        }
        else if (errRes.errorCode == '103') {
            // this.dailyDealAddToCartErrors = 'maximum-limit-per-customer';
            return;
        }
        else if (errRes.errorCode == '102') {
            // this.dailyDealAddToCartErrors = 'maximum-limit-over-period';
            return;
        }
        else if (errRes.errorCode == '101') {
            // this.dailyDealAddToCartErrors = 'maximum-limit';
            return;
        }
        else if (errRes.errorCode == '401') {
            // this.dailyDealAddToCartErrors = 'daily-deal-in-cart';
            return;
        }
        else if (errRes.errorCode == '201') {
            // this.dailyDealAddToCartErrors = 'insufficient-quantity';
            return;
        }
        else if (errRes.errorCode == '501') {
            // this.dailyDealAddToCartErrors = 'not-available';
            return;
        }
        else if (errRes.errorCode == '301') {
            // this.dailyDealAddToCartErrors = 'prize-changed';
            return;
        }
    }
    getShoppingCartData() {
        this._httpService
            .get(`/digital/customers/creditCards/rewards/view/shoppingCart`)
            .subscribe(async (res) => {
            this.scr = await res;
            this.retrivedCartBs$.next(this.scr);
            console.log("Getting shopping cart details", res);
        }, error => {
            this.retrivedCartBs$.next(this.retrievedCart);
            console.log("Error retriving shopping cart", error);
        });
    }
    // API to add items to the shopping cart. This API invokes the branded cards service to add items to the shopping cart from augeo backend.
    addItemToCart(payload) {
        this._httpService
            .post(`/digital/customers/creditCards/rewards/add/shoppingCart`, payload)
            .subscribe({
            next: async (data) => {
                this.scr = await data;
                this.retrivedCartBs$.next(this.scr);
                this.isLoading$.next(false);
                console.log("Adding cart item success", data);
                this.disableCheckOut$.next(false);
            },
            error: error => {
                this.isLoading$.next(false);
                this.disableCheckOut$.next(true);
                console.log("Failure adding cart item", error);
            }
        });
    }
    //API to retrieve the catalog card list. This API invokes the branded cards service to fetch the gift cards from augeo backend
    getAllcatalogCardList() {
        const payload = {
            "cardType": "DEALS_GIFTCARD",
            "accountId": this.accountId
        };
        this._httpService
            .post(`/digital/customers/creditCards/accounts/rewards/catalogCardList`, payload)
            .subscribe({
            next: async (data) => {
                this.catalogCardList = await data;
                this.catalogCardList$.next(this.catalogCardList);
                this.isLoading$.next(false);
                console.log("API to retrieve the catalog card list", data);
            },
            error: error => {
                this.isLoading$.next(false);
                console.log("Failure adding cart item", error);
            }
        });
    }
    // API to retrieve the singular catalog card detailed information
    getcatalogCardDetails(data) {
        const payload = {
            "accountId": this.accountId,
            "merchantProductIdentifier": data.merchantProductIdentifier,
            "cardType": data.cardType
        };
        this._httpService
            .post(`/digital/customers/creditCards/accounts/rewards/merchants/catalogCardDetails`, payload)
            .subscribe({
            next: data => {
                this.addItemToCart({
                    "denominationIdentifier": data.catalogCardDetailedInfo?.product[0]?.productDenomination[0]?.denominationIdentifer,
                    "itemQuantity": 1
                });
                console.log("API to retrieve the catalog card list", data);
            },
            error: error => {
                this.catalogCardDetails$.next(error);
                this.disableCheckOut$.next(true);
                this.isLoading$.next(false);
                console.log("Failure adding cart item", error);
            }
        });
    }
    updateItemToCart(payload) {
        return this._httpService
            .put(`/digital/customers/creditCards/rewards/update/shoppingCart`, payload)
            .pipe(catchError(err => {
            return throwError(err);
        }));
    }
    deleteItemToCart(payload) {
        const options = {};
        options.headers = {
            itemIdentifier: `${payload.itemIdentifier}`
        };
        return this._httpService
            .delete(`/digital/customers/creditCards/rewards/delete/shoppingCart?denominationIdentifier=${payload.denominationIdentifier}`, options)
            .pipe(catchError(err => {
            return throwError(err);
        }));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShoppingCartService, deps: [{ token: i1.HttpService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShoppingCartService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: ShoppingCartService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpService }] });

/**
 * module CoreServicesModule
 * main CoreServicesModule
 * preferred
 **/
/** The CoreServicesModule module contains the core services. **/
/**
 * The CoreServicesModule module contains the core services.
 * @class CoreServicesModule
 * @internal
 **/
class CoreServicesModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('CoreServicesModule is already loaded. Import it in the AppModule only');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CoreServicesModule, deps: [{ token: CoreServicesModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.14", ngImport: i0, type: CoreServicesModule, imports: [CoreCommonModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CoreServicesModule, providers: [
            AppStateService,
            provideNgxWebstorage(withNgxWebstorageConfig({
                prefix: '',
                separator: '',
                caseSensitive: true
            }), withLocalStorage(), withSessionStorage()),
            AppConfigService,
            FeatureDetectionService,
            StorageService,
            HttpConfigService,
            ContentStorageService,
            ContentStaticService,
            HttpService,
            ContentService,
            ContentResolve,
            SessionService,
            EventHubProducerService,
            ShoppingCartService
        ], imports: [CoreCommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.14", ngImport: i0, type: CoreServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CoreCommonModule],
                    providers: [
                        AppStateService,
                        provideNgxWebstorage(withNgxWebstorageConfig({
                            prefix: '',
                            separator: '',
                            caseSensitive: true
                        }), withLocalStorage(), withSessionStorage()),
                        AppConfigService,
                        FeatureDetectionService,
                        StorageService,
                        HttpConfigService,
                        ContentStorageService,
                        ContentStaticService,
                        HttpService,
                        ContentService,
                        ContentResolve,
                        SessionService,
                        EventHubProducerService,
                        ShoppingCartService
                    ]
                }]
        }], ctorParameters: () => [{ type: CoreServicesModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

/*
 * Public API Surface of citi-common-modules
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppStateService, CoreAppModule, CoreCommonModule, CoreDecorateModuleRef, CoreServicesModule, EventHubActionProducerDirective, EventHubProducerService, ShoppingCartService };
